import {ReactElement, useCallback, useMemo, useState} from "react";
import {useQuery} from "@apollo/client";
import {useLocation, useNavigate, useParams} from "react-router";
import {Dayjs} from "dayjs";

import {Button} from "../../components/input";
import {Loading} from "../../components/loading";
import {HidablePanel} from "../../components/hidable-panel";
import {Page, usePageInput} from "../../layout";
import {CollectionEditor} from "./collection-editor";
import {
	ADD_POST_FROM_RSS_FEED,
	Collection,
	GET_COLLECTION,
	MOVE_POST,
	MovePostVars,
	Post,
	SAVE_COLLECTION,
	loadCollection,
	ADD_POST_FROM_SUGGESTED_FEED,
} from "../../data";
import {useMutationToast} from "../../toast";
import {useDirtyCopy} from "../../dirty-copy";
import {ContentLibrary} from "./content-library";
import {useCollectionModal} from "./collection-modal";
import {useCollectionConfirmModal} from "./collection-confirm-modal";

export type ModalType = "schedule" | "send";

const Panel = (): ReactElement => {
	const {id: collectionId} = useParams();
	const [addPostFromSuggestedFeed] = useMutationToast(ADD_POST_FROM_SUGGESTED_FEED);
	const [addPostFromRssFeed] = useMutationToast(ADD_POST_FROM_RSS_FEED);

	const onAddToCollection = useCallback(
		async ({shareId, feedId}: {shareId?: number; feedId?: number | undefined}) => {
			if (!feedId) {
				return addPostFromSuggestedFeed({
					variables: {id: collectionId, postId: shareId, postType: "recommended"},
				});
			}
			return addPostFromRssFeed({
				variables: {id: collectionId, postId: shareId},
			});
		},
		[collectionId, addPostFromSuggestedFeed, addPostFromRssFeed]
	);

	return <ContentLibrary type="Collection" onAdd={onAddToCollection} />;
};

export const EditCollection = (): ReactElement => {
	const navigate = useNavigate();
	const [saved, setSaved] = useState(false);
	const {id} = useParams();
	const {data, loading: init} = useQuery(GET_COLLECTION, {variables: {id}});
	const [saveCollection, {loading}] = useMutationToast(SAVE_COLLECTION);
	const [movePost] = useMutationToast(MOVE_POST);
	const collection = useMemo(
		() =>
			data?.collection
				? loadCollection(data.collection)
				: ({
						posts: [] as Post[],
						autoLike: {},
						to: {
							groupIds: [] as number[],
							virtualAssistantIds: [] as number[],
							companyIds: [] as number[],
							slackChannels: [] as string[],
						},
				  } as Collection),
		[data?.collection]
	);

	const onUpdate = useCallback(
		({val, discard, changes: baseChanges}) => {
			let changes = {...baseChanges};
			if (baseChanges?.to) changes = {...baseChanges, ...baseChanges.to};
			delete changes.__typename;
			delete changes.to;
			return saveCollection({
				variables: {id: val.id, changes},
				onCompleted: () => {
					setSaved(true);
					discard();
				},
			});
		},
		[saveCollection]
	);

	const {val, update, dirty, flush} = useDirtyCopy(collection, {debounce: 2000, onUpdate});

	const {open: confirm} = useCollectionConfirmModal({collection: val});

	const handleMovePost = useCallback((variables: MovePostVars) => flush().then(() => movePost({variables})), [
		flush,
		movePost,
	]);

	const approveCollections = useCallback(
		(scheduledFor: Dayjs | undefined, type: ModalType, close: () => void) => {
			saveCollection({variables: {id: collection.id, changes: {scheduledFor, status: "APPROVED"}}}).then(
				() => {
					type === "send"
						? flush().then(() => {
								close();
								confirm();
						  })
						: navigate("/collections");
				}
			);
		},
		[collection.id, saveCollection, flush, confirm, navigate]
	);

	const {open: schedule} = useCollectionModal({
		collection: val,
		onApprove: approveCollections,
		type: "schedule",
	});

	const {open: sendNow} = useCollectionModal({
		collection: val,
		onApprove: approveCollections,
		type: "send",
	});
	const disabled = useMemo(() => !!(val.status === "approved" && val.scheduledFor?.isBefore()), [
		val.status,
		val.scheduledFor,
	]);

	const handleSave = useCallback(() => flush().then(() => navigate("/collections")), [navigate, flush]);
	const handleSchedule = useCallback(() => flush().then(schedule), [flush, schedule]);
	const handleSend = useCallback(() => flush().then(sendNow), [flush, sendNow]);

	const recipientCount = useMemo(
		() =>
			Object.values(val.to)
				.filter(Array.isArray)
				.reduce((sum, array) => sum + (array?.length ?? 0), 0),
		[val.to]
	);
	const hasPosts = useMemo(() => val.posts.length > 0, [val.posts]);

	usePageInput(
		() =>
			disabled ? (
				<></>
			) : (
				<Button
					onClick={handleSave}
					value="Save and Exit"
					color="black"
					invert
					border={false}
					loading={loading}
				/>
			),
		[loading, handleSave, disabled]
	);
	usePageInput(
		() =>
			disabled ? (
				<></>
			) : (
				<Button
					onClick={handleSchedule}
					value="Approve and Schedule"
					invert
					disabled={!recipientCount || !hasPosts}
				/>
			),
		[disabled, handleSchedule, recipientCount, hasPosts]
	);
	usePageInput(
		() =>
			disabled ? (
				<></>
			) : (
				<Button onClick={handleSend} value="Approve and Send Now" disabled={!recipientCount || !hasPosts} />
			),
		[disabled, handleSend, recipientCount, hasPosts]
	);

	const {state} = useLocation();
	const returnTo = state?.from || "/collections";

	return (
		<Page title="Collection" returnTo={returnTo}>
			{init ? (
				<Loading position="center" />
			) : val.sent ? (
				<CollectionEditor
					collection={val}
					onChange={update}
					saved={loading ? "saving" : saved && !dirty ? "saved" : undefined}
					movePost={handleMovePost}
					disabled={disabled}
				/>
			) : (
				<HidablePanel Panel={Panel} what="Library">
					<CollectionEditor
						collection={val}
						onChange={update}
						saved={loading ? "saving" : saved && !dirty ? "saved" : undefined}
						movePost={handleMovePost}
						disabled={disabled}
					/>
				</HidablePanel>
			)}
		</Page>
	);
};
